import { useState, useEffect, useCallback } from "react"
import { Container, Row, Col, Spinner, Card, Table, Badge, Form } from "react-bootstrap"
import { useTranslation } from "react-i18next"
import API from "../../common/API"
import { parsedUser } from "../../common/GetCurrentUser"
import { isSuperAdmin } from "../../common/helpers"
import DateRangeFilter from "../../components/indicators/DateRangeFilter"
import SuperAdminBackLink from "../../components/platform/SuperAdminBackLink"
import moment from "moment"
import { DispatchDetailDependency } from "../../types/Indicators.types"

interface Props {
  embedded?: boolean
}

interface OrgOption { id: string; name: string }

export default function DispatchByDependencyPage({ embedded = false }: Props) {
  const { t } = useTranslation("global")
  const user: any = parsedUser()
  const userOrgId = user?.OrganizationId || user?.organizationId || ""
  const canSelectOrg = isSuperAdmin(user)

  const [isLoading, setIsLoading] = useState(false)
  const [organizations, setOrganizations] = useState<OrgOption[]>([])
  const [isLoadingOrgs, setIsLoadingOrgs] = useState(false)
  const [selectedOrgId, setSelectedOrgId] = useState(canSelectOrg ? "" : userOrgId)
  const [data, setData] = useState<DispatchDetailDependency[]>([])
  const [startDate, setStartDate] = useState(moment().startOf("month").format("YYYY-MM-DD"))
  const [endDate, setEndDate] = useState(moment().format("YYYY-MM-DD"))

  const effectiveOrgId = canSelectOrg ? selectedOrgId : userOrgId

  const loadOrgs = useCallback(async () => {
    if (!canSelectOrg) return
    try {
      setIsLoadingOrgs(true)
      const res = await API.getAction("Organization/AdminList", "Page=0&Quantity=200")
      if (res.data.status === "ok") setOrganizations(res.data.response ?? [])
    } catch (ex) { console.error(ex) }
    finally { setIsLoadingOrgs(false) }
  }, [canSelectOrg])

  const bindData = useCallback(async (start: string, end: string) => {
    if (!effectiveOrgId) return
    try {
      setIsLoading(true)
      setStartDate(start)
      setEndDate(end)
      const params = `organizationId=${effectiveOrgId}&startDate=${start}&endDate=${end}`
      const res = await API.getAction("Indicators/DispatchDetailByDependency", params)
      if (res.data.status === "ok") setData(res.data.response ?? [])
    } catch (ex) { console.error(ex) }
    finally { setIsLoading(false) }
  }, [effectiveOrgId])

  useEffect(() => { loadOrgs() }, [loadOrgs])

  useEffect(() => {
    setData([])
    if (effectiveOrgId) bindData(startDate, endDate)
  }, [effectiveOrgId])

  return (
    <Container fluid className="px-4 py-3">
      {!embedded && <SuperAdminBackLink />}

      <h5 className="mb-3">
        <i className="fas fa-map-location-dot me-2 text-warning" />
        {t("By Dependency / Zone")}
      </h5>

      {canSelectOrg && (
        <Row className="g-2 mb-3">
          <Col md={4} lg={3}>
            <Form.Select
              size="sm"
              value={selectedOrgId}
              onChange={e => setSelectedOrgId(e.target.value)}
              disabled={isLoadingOrgs}
            >
              {!selectedOrgId && <option value="">{t("Select Organization")}</option>}
              {organizations.map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
            </Form.Select>
          </Col>
        </Row>
      )}

      <DateRangeFilter onFilter={(s, e) => bindData(s, e)} loading={isLoading} />

      {canSelectOrg && !selectedOrgId ? (
        <Card className="border-0 shadow-sm">
          <Card.Body className="py-4 text-center text-muted">
            {t("Select an organization to load dispatch indicators")}
          </Card.Body>
        </Card>
      ) : isLoading ? (
        <div className="text-center py-5"><Spinner animation="border" variant="primary" /></div>
      ) : (
        <Card className="shadow-sm border-0">
          <Card.Body className="p-0">
            <Table hover responsive className="mb-0">
              <thead className="table-light">
                <tr>
                  <th className="ps-3">{t("Dependency")}</th>
                  <th className="text-end">{t("Total Qty")}</th>
                  <th className="text-end">{t("Dispatches")}</th>
                  <th className="text-end">{t("Products")}</th>
                  <th className="text-end pe-3">{t("Clients")}</th>
                </tr>
              </thead>
              <tbody>
                {data.length === 0 && (
                  <tr><td colSpan={5} className="text-center text-muted py-4">{t("No data available")}</td></tr>
                )}
                {data.map((d, i) => (
                  <tr key={i}>
                    <td className="ps-3 fw-semibold">{d.dependency}</td>
                    <td className="text-end">{d.totalQuantity.toLocaleString()}</td>
                    <td className="text-end"><Badge bg="secondary">{d.dispatchCount}</Badge></td>
                    <td className="text-end"><Badge bg="light" text="dark">{d.distinctProducts}</Badge></td>
                    <td className="text-end pe-3"><Badge bg="light" text="dark">{d.distinctClients}</Badge></td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      )}
    </Container>
  )
}
